import { george, observer, pr, React, theme, ui } from "aaa";
import { addressToString, ContactsStore } from "stores/ContactsStore";

type IAddressOption = { value: string, label: string };

const ADD_NEW_KEY = "addNew";

const getAdresses = (contactsStore: ContactsStore) => {
    if (contactsStore.addresses.length > 0) {
        return contactsStore.addresses
            .filter(address => contactsStore.mainAddressOnly ? address.flags?.includes("mainContact") : true)
            .map(contact => ({
                value: contact.id,
                label: addressToString(contact)
            }));
    } else {
        if (contactsStore.mainAddressOnly && contactsStore.invalidPrimaryAddress) {
            return [{
                value: contactsStore.invalidPrimaryAddress.id,
                label: addressToString(contactsStore.invalidPrimaryAddress)
            }];
        }
    }

    return [];
};

const getConfiguration = (contactsStore: ContactsStore, description?: string, noAddressMessage?: string) => {
    const addresses = getAdresses(contactsStore);
    const showNoAddress = !contactsStore.isLoading && addresses.length === 0 && noAddressMessage;
    const showDescription = !showNoAddress && description;

    return {
        addresses,
        showNoAddress,
        showDescription,
    };
};

const ChooseAddressDropdown = observer(({
    contactsStore,
    items,
    onNewAddress,
    onUpdateAddress,
    onChangeAddress,
    selectedAddressId,
}: {
    contactsStore: ContactsStore,
    items: IAddressOption[],
    onNewAddress?: () => void,
    onUpdateAddress?: () => void,
    onChangeAddress?: (addressId: string) => void,
    selectedAddressId?: string,
}) => {
    const ADD_NEW_OPTION = {
        value: ADD_NEW_KEY,
        label: ui.__("address.addNew")
    };

    const options = [
        ...items,
        ...(contactsStore.mainAddressOnly || !onNewAddress ? [] : [ADD_NEW_OPTION])
    ];

    let currentValue;

    if (onChangeAddress) {
        currentValue = selectedAddressId;
    } else {
        currentValue = contactsStore.mainAddressOnly
            ? contactsStore.selectedAddressId
            : contactsStore.invalidPrimaryAddress?.id ?? contactsStore.selectedAddressId;
    }

    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ flexGrow: 1 }}>
                <george.DropDown
                    items={options}
                    isLoading={contactsStore.isLoading}
                    value={currentValue ? currentValue : options[0]?.value}
                    onChange={(event: any, index: number, value: string) => {
                        if (value === ADD_NEW_KEY && onNewAddress) {
                            onNewAddress();
                        } else if (onChangeAddress) {
                            onChangeAddress(value);
                        } else {
                            contactsStore.selectedAddressId = value;
                        }
                    }}
                    onClick={() => {
                        // Hack for a11y: We want to trigger new address when using native rendering
                        if (options.length === 1 && options[0].value === ADD_NEW_KEY && onNewAddress) {
                            onNewAddress();
                        }
                    }}
                />
            </div>
            {/* {contactsStore.invalidPrimaryAddress && !!items.find(item => item.value === contactsStore.invalidPrimaryAddress?.id) && onUpdateAddress && */}
            {onUpdateAddress &&
                <div
                    role="button"
                    onClick={onUpdateAddress}
                    tabIndex={0}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 54,
                        height: 54,
                        borderRadius: theme.BORDER_RADIUS,
                        marginLeft: theme.MARGIN_SMALL,
                        border: `1px solid ${theme.customColors.georgeInputFrame}`,
                        cursor: "pointer",
                        color: ui.isDarkMode ? "white" : "#0065AC"
                    }}
                >
                    <pr.IconPen />
                </div>
            }
        </div>
    );
});

export const ChooseAddressEmbedded = observer(({
    contactsStore,
    description,
    noAddressMessage,
    onNewAddress,
    onUpdateAddress,
    onChangeAddress,
    selectedAddressId,
    hideTitle,
}: {
    contactsStore: ContactsStore,
    description?: string,
    noAddressMessage?: string,
    onNewAddress?: () => void,
    onUpdateAddress?: () => void,
    onChangeAddress?: (addressId: string) => void,
    selectedAddressId?: string,
    hideTitle?: boolean,
}) => {
    const { addresses, showNoAddress, showDescription } = getConfiguration(contactsStore, description, noAddressMessage);

    return (
        <>
            {!hideTitle && (
                <george.SectionTitle>{ui.__("shop.personalData.address.title")}</george.SectionTitle>
            )}
            {showNoAddress &&
                <george.SectionSubtitleTop>
                    {noAddressMessage}
                </george.SectionSubtitleTop>
            }
            {showDescription &&
                <george.SectionSubtitleTop>
                    {description}
                </george.SectionSubtitleTop>
            }
            {!showNoAddress &&
                <ChooseAddressDropdown
                    contactsStore={contactsStore}
                    items={addresses}
                    onNewAddress={onNewAddress}
                    onUpdateAddress={onUpdateAddress}
                    onChangeAddress={onChangeAddress}
                    selectedAddressId={selectedAddressId}
                />
            }
        </>
    );
});

export const ChooseAddress = observer(({
    contactsStore,
    description,
    noAddressMessage,
    onNewAddress,
    onUpdateAddress,
    onChangeAddress,
    selectedAddressId,
    hideTitle,
}: {
    contactsStore: ContactsStore,
    description?: string,
    noAddressMessage?: string,
    onNewAddress?: () => void,
    onUpdateAddress?: () => void,
    onChangeAddress?: (addressId: string) => void,
    selectedAddressId?: string,
    hideTitle?: boolean,
}) => {
    const { addresses, showNoAddress, showDescription } = getConfiguration(contactsStore, description, noAddressMessage);

    return (
        <george.Card>
            {!hideTitle && (
                <george.CardSectionTitle title={ui.__("shop.personalData.address.title")} />
            )}
            <george.CardSectionContent>
                {showNoAddress &&
                    <george.SectionSubtitleTop>
                        {noAddressMessage}
                    </george.SectionSubtitleTop>
                }
                {showDescription &&
                    <george.SectionSubtitleTop>
                        {description}
                    </george.SectionSubtitleTop>
                }
                {!showNoAddress &&
                    <ChooseAddressDropdown
                        contactsStore={contactsStore}
                        items={addresses}
                        onNewAddress={onNewAddress}
                        onUpdateAddress={onUpdateAddress}
                        onChangeAddress={onChangeAddress}
                        selectedAddressId={selectedAddressId}
                    />
                }
            </george.CardSectionContent >
        </george.Card>
    );
}
);

export const ChooseAddressResponsive = observer(({
    contactsStore,
    description,
    noAddressMessage,
    onNewAddress,
    onUpdateAddress,
    onChangeAddress,
    selectedAddressId,
    icon,
}: {
    contactsStore: ContactsStore,
    description?: string,
    noAddressMessage?: string,
    onNewAddress?: () => void,
    onUpdateAddress?: () => void,
    onChangeAddress?: (addressId: string) => void,
    selectedAddressId?: string,
    icon?: React.ReactNode,
}) => {
    const { addresses, showNoAddress, showDescription } = getConfiguration(contactsStore, description, noAddressMessage);
    const showTitle = showNoAddress || showDescription;

    return (
        <>
            {showTitle &&
                <george.SectionTitle style={{ marginTop: 0 }}>
                    {ui.__("shop.personalData.address.title")}
                </george.SectionTitle>
            }
            {showNoAddress &&
                <george.SectionSubtitleTop>
                    {noAddressMessage}
                </george.SectionSubtitleTop>
            }
            {showDescription &&
                <george.SectionSubtitleTop>
                    {description}
                </george.SectionSubtitleTop>
            }
            {!showNoAddress &&
                <george.ResponsiveFormElement label={
                    icon ?
                        <pr.HFlex style={{ alignItems: "center", gap: 8 }}>
                            {icon}
                            {ui.__("common.address")}
                        </pr.HFlex>
                        : ui.__("common.address")}
                >
                    <ChooseAddressDropdown
                        contactsStore={contactsStore}
                        items={addresses}
                        onNewAddress={onNewAddress}
                        onUpdateAddress={onUpdateAddress}
                        onChangeAddress={onChangeAddress}
                        selectedAddressId={selectedAddressId}
                    />
                </george.ResponsiveFormElement>
            }
        </>
    );
});



// WEBPACK FOOTER //
// ./src/components/common/ChooseAddress.tsx